/**
 * SeasonalConfirm — 가격 변경 요청 전용 페이지
 * 이메일의 "Request Price Change" 링크에서만 접근
 * Accept / Hold 는 이메일에서 서버 직접 처리
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api/api';
import {
  Box, Typography, Button, CircularProgress, Paper,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, InputAdornment, Chip,
} from '@mui/material';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon  from '@mui/icons-material/ErrorOutline';
import EditIcon          from '@mui/icons-material/Edit';

const BROWN  = '#8B4513';
const BROWN2 = '#6b3410';
const BG     = '#faf6f1';

function fmtDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function centsToStr(c) {
  if (c == null) return '';
  return (c / 100).toFixed(2);
}

function hasSL(item) {
  return item.price_s != null && item.price_l != null;
}

function Wrap({ children }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG, py: 5, px: 2 }}>
      {children}
      <Typography sx={{ textAlign: 'center', mt: 3, color: '#ccc', fontSize: 11 }}>
        Digntec Signage CMS · support@digntec.com
      </Typography>
    </Box>
  );
}

export default function SeasonalConfirm() {
  const { token } = useParams();

  const [loading, setLoading]     = useState(true);
  const [info, setInfo]           = useState(null);
  const [error, setError]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]           = useState(false);

  // 아이템별 변경 가격 입력: { [itemName]: { s: '', l: '', single: '' } }
  const [inputs, setInputs] = useState({});

  useEffect(() => {
    if (!token) { setError('Invalid link.'); setLoading(false); return; }
    api.get(`/seasonal/confirm/${token}`)
      .then(res => {
        setInfo(res.data);
        const init = {};
        (res.data.menu?.items || []).forEach(item => {
          init[item.name] = { s: '', l: '', single: '' };
        });
        setInputs(init);
      })
      .catch(err => setError(err.response?.data?.message || 'Failed to load this link.'))
      .finally(() => setLoading(false));
  }, [token]);

  const setField = (name, field, val) =>
    setInputs(prev => ({ ...prev, [name]: { ...prev[name], [field]: val } }));

  const buildItemPrices = () =>
    (info?.menu?.items || []).map(item => {
      const inp = inputs[item.name] || {};
      if (hasSL(item)) {
        return {
          itemName:     item.name,
          finalPrice_s: inp.s ? Math.round(parseFloat(inp.s) * 100) : item.price_s,
          finalPrice_l: inp.l ? Math.round(parseFloat(inp.l) * 100) : item.price_l,
        };
      }
      return {
        itemName:   item.name,
        finalPrice: inp.single ? Math.round(parseFloat(inp.single) * 100) : item.basePrice,
      };
    });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/seasonal/confirm/${token}`, {
        participating: true,
        itemPrices: buildItemPrices(),
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── 로딩 ──
  if (loading) return (
    <Wrap>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 12 }}>
        <CircularProgress sx={{ color: BROWN }} />
      </Box>
    </Wrap>
  );

  // ── 오류 ──
  if (error && !info) return (
    <Wrap>
      <Paper sx={{ maxWidth: 480, margin: '40px auto', p: 4, textAlign: 'center', borderRadius: 3 }}>
        <ErrorOutlineIcon sx={{ fontSize: 56, color: '#c62828', mb: 1 }} />
        <Typography variant="h6" fontWeight={700} mb={1}>Link Error</Typography>
        <Typography color="text.secondary">{error}</Typography>
      </Paper>
    </Wrap>
  );

  const { store, menu, alreadyConfirmed, confirmedAt } = info || {};

  // ── 이미 완료 ──
  if (alreadyConfirmed && !done) return (
    <Wrap>
      <Paper sx={{ maxWidth: 480, margin: '40px auto', p: 4, textAlign: 'center', borderRadius: 3 }}>
        <CheckCircleIcon sx={{ fontSize: 56, color: '#2e7d32', mb: 1 }} />
        <Typography variant="h6" fontWeight={700} mb={1}>Already Confirmed</Typography>
        <Typography color="text.secondary">
          {store?.name} confirmed on {fmtDate(confirmedAt)}.
        </Typography>
      </Paper>
    </Wrap>
  );

  // ── 제출 완료 ──
  if (done) return (
    <Wrap>
      <Paper sx={{ maxWidth: 480, margin: '40px auto', p: 4, textAlign: 'center', borderRadius: 3 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: BROWN, mb: 1 }} />
        <Typography variant="h5" fontWeight={700} mb={1}>Price Change Submitted!</Typography>
        <Typography color="text.secondary" mb={2}>
          Your requested prices have been sent to HQ for review.<br/>
          <strong>{store?.name}</strong>
        </Typography>
        <Chip label={menu?.name?.replace('[TEST] ', '')} sx={{ background: '#f3ebe3', color: BROWN, fontWeight: 700 }} />
      </Paper>
    </Wrap>
  );

  const items = menu?.items || [];

  // ── 메인: 가격 변경 요청 폼 ──
  return (
    <Wrap>
      <Paper sx={{ maxWidth: 720, margin: '0 auto', borderRadius: 3, overflow: 'hidden' }}>

        {/* 헤더 */}
        <Box sx={{ background: BROWN, px: 4, py: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <EditIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 20 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 600 }}>
              Price Change Request
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight={800} color="white">
            {menu?.name?.replace('[TEST] ', '')}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, mt: 0.5 }}>
            {store?.name} · {fmtDate(menu?.startDate)} — {fmtDate(menu?.endDate)}
          </Typography>
        </Box>

        <Box sx={{ px: 4, py: 3.5 }}>
          <Typography sx={{ fontSize: 15, color: '#444', lineHeight: 1.7, mb: 3 }}>
            Enter your preferred price in the <strong>"My Price"</strong> column.
            Leave blank to keep the HQ suggested price.
          </Typography>

          {/* 가격표 */}
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow sx={{ background: '#f3ebe3' }}>
                <TableCell sx={{ fontWeight: 700, color: BROWN, fontSize: 13 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 700, color: BROWN, fontSize: 13, textAlign: 'center' }}>
                  HQ Suggested
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: BROWN, fontSize: 13, textAlign: 'center' }}>
                  My Price
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, idx) => {
                const inp = inputs[item.name] || {};
                const sl  = hasSL(item);
                return (
                  <TableRow key={idx} hover sx={{ '&:hover': { background: '#fdf8f3' } }}>

                    {/* 메뉴명 */}
                    <TableCell sx={{ py: 2 }}>
                      <Typography fontWeight={700} fontSize={14}>{item.name}</Typography>
                      {item.note && (
                        <Chip label={item.note} size="small"
                          sx={{ mt: 0.3, fontSize: 10, background: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                      )}
                    </TableCell>

                    {/* HQ 가격 */}
                    <TableCell sx={{ textAlign: 'center', py: 2 }}>
                      {sl ? (
                        <Box>
                          <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#555' }}>
                            S ${centsToStr(item.price_s)} &nbsp;/&nbsp; L ${centsToStr(item.price_l)}
                          </Typography>
                          {item.calories_s != null && (
                            <Typography sx={{ fontSize: 11, color: '#bbb', mt: 0.3 }}>
                              {item.calories_s}cal (S) · {item.calories_l}cal (L)
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#555' }}>
                          ${centsToStr(item.basePrice)}
                        </Typography>
                      )}
                    </TableCell>

                    {/* 변경 가격 입력 */}
                    <TableCell sx={{ textAlign: 'center', py: 2 }}>
                      {sl ? (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <TextField
                            size="small" label="S" type="number"
                            value={inp.s}
                            onChange={e => setField(item.name, 's', e.target.value)}
                            placeholder={centsToStr(item.price_s)}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            sx={{ width: 110 }}
                            inputProps={{ min: 0, step: 0.05 }}
                          />
                          <TextField
                            size="small" label="L" type="number"
                            value={inp.l}
                            onChange={e => setField(item.name, 'l', e.target.value)}
                            placeholder={centsToStr(item.price_l)}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            sx={{ width: 110 }}
                            inputProps={{ min: 0, step: 0.05 }}
                          />
                        </Box>
                      ) : (
                        <TextField
                          size="small" label="Price" type="number"
                          value={inp.single}
                          onChange={e => setField(item.name, 'single', e.target.value)}
                          placeholder={centsToStr(item.basePrice)}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          sx={{ width: 130 }}
                          inputProps={{ min: 0, step: 0.05 }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Box sx={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 2, px: 2, py: 1.5, mb: 3 }}>
            <Typography sx={{ fontSize: 13, color: '#7c5c00' }}>
              💡 Blank fields will use the HQ suggested price. Changes will be reviewed by HQ before taking effect.
            </Typography>
          </Box>

          {error && (
            <Typography sx={{ color: '#c62828', fontSize: 13, mb: 2 }}>{error}</Typography>
          )}

          <Button
            fullWidth variant="contained" size="large"
            disabled={submitting}
            onClick={handleSubmit}
            sx={{
              background: BROWN, fontWeight: 700, fontSize: 16,
              py: 1.8, borderRadius: 2,
              '&:hover': { background: BROWN2 },
            }}
          >
            {submitting
              ? <CircularProgress size={22} sx={{ color: 'white' }} />
              : 'Submit Price Change Request →'}
          </Button>
        </Box>
      </Paper>
    </Wrap>
  );
}
